import { ReactElement, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { LoadingIndicator } from '@havenengineering/module-shared-library/dist/components/LoadingIndicator';
import {
  ProgressStep,
  ProgressStepper,
} from '@havenengineering/module-shared-owners-ui/dist/components/ProgressStepper';

import { fetchWrapper, withApiBaseUrl } from '../../helpers/fetch';
import {
  getAddBreaksPath,
  getRenewalEntryPoint,
  getReviewBreaksPath,
  withRenewalEntryPoint,
} from '../../helpers/renewalEntryPoint';
import { DirectDebitBalanceType } from '../../types/finance/directDebit/directDebit';
import { LettingConfigEntry } from '../../types/lettingConfig';
import { displayOneOffPayment } from './helpers';
import styles from './Let2OwnProgressStepper.module.scss';

export enum Let2OwnRenewalSteps {
  ADD_BREAKS = 'addBreaks',
  REVIEW_BREAKS = 'reviewBreaks',
  ONE_OFF_PAYMENT = 'oneOffPayment',
  DIRECT_DEBIT = 'directDebit',
  CONFIRM_DETAILS = 'confirmDetails',
}

type Props = {
  currentStep: Let2OwnRenewalSteps;
  handleClick?: (step: Let2OwnRenewalSteps) => void;
  currentDate: string;
  complete?: boolean;
  skipPayment?: boolean;
};

export const Let2OwnProgressStepper = ({
  currentStep,
  handleClick,
  currentDate,
  complete = false,
  skipPayment = false,
}: Props): ReactElement => {
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(true);
  const [partialPaymentConfig, setPartialPaymentConfig] = useState<
    LettingConfigEntry[] | undefined
  >([]);

  const onStepClick = (step: Let2OwnRenewalSteps) => {
    const entryPoint = getRenewalEntryPoint(router.query);
    switch (step) {
      case Let2OwnRenewalSteps.ADD_BREAKS:
        router.push(getAddBreaksPath(entryPoint));
        break;
      case Let2OwnRenewalSteps.REVIEW_BREAKS:
        router.push(getReviewBreaksPath(entryPoint));
        break;
      case Let2OwnRenewalSteps.ONE_OFF_PAYMENT:
        router.push(
          withRenewalEntryPoint('/finance/partial-payment', entryPoint)
        );
        break;
      case Let2OwnRenewalSteps.DIRECT_DEBIT:
        router.push(
          withRenewalEntryPoint(
            `/finance/direct-debit/setup?balanceType=${DirectDebitBalanceType.Balance}`,
            entryPoint
          )
        );
        break;
    }
  };

  const fetchLettingConfig = async () => {
    try {
      const config = await fetchWrapper(
        withApiBaseUrl(
          '/letting/letting-config?name=let2ownPartialPaymentDateRange'
        ),
        {
          method: 'GET',
          credentials: 'include',
        }
      );
      setPartialPaymentConfig(config);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLettingConfig();
  }, []);

  const RENEWAL_PROGRESS_STEPS = [
    {
      label: 'Add your breaks',
      step: Let2OwnRenewalSteps.ADD_BREAKS,
    },
    {
      label: 'Review breaks',
      step: Let2OwnRenewalSteps.REVIEW_BREAKS,
    },
    ...(displayOneOffPayment(partialPaymentConfig, currentDate) && !skipPayment
      ? [
          {
            label: 'One-off payment',
            step: Let2OwnRenewalSteps.ONE_OFF_PAYMENT,
          },
        ]
      : []),
    ...(!skipPayment
      ? [
          {
            label: 'Set up Direct Debit',
            step: Let2OwnRenewalSteps.DIRECT_DEBIT,
          },
        ]
      : []),
    {
      label: 'Confirm details',
      step: Let2OwnRenewalSteps.CONFIRM_DETAILS,
    },
  ] as ProgressStep<Let2OwnRenewalSteps>[];

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingIndicator loading />
      </div>
    );
  }

  return (
    <div className={styles.renewalStepper}>
      <ProgressStepper
        steps={RENEWAL_PROGRESS_STEPS}
        currentStep={currentStep}
        handleClick={handleClick ?? onStepClick}
        complete={complete}
      />
    </div>
  );
};
